import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  Button, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  Card, CardContent, Badge, Dialog,
  DialogTitle, DialogContent, DialogActions,
  TextField
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  ArrowBack as BackIcon,
  AccessTime as TimeIcon,
  Info as InfoIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types
interface Waiter {
  user_id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  roleID: number;
}

interface AttendanceRecord {
  id: number;
  attendance_date: string;
  waiter_id: string;
  waiter_name?: string;
  supervisor_id: string;
  status: 'present';
  check_in_time: string;
  check_out_time: string | null;
  createdBy?: string;
  createdAt?: string;
}

interface WaiterAttendanceStatus {
  waiterId: string;
  hasCheckedIn: boolean;
  hasCheckedOut: boolean;
  record?: AttendanceRecord;
  checkInTime?: string;
  checkOutTime?: string | null;
}

interface TimeDialogProps {
  open: boolean;
  waiterId: string;
  waiterName: string;
  type: 'checkin' | 'checkout';
  initialTime?: string;
  onClose: () => void;
  onConfirm: (waiterId: string, time: string) => void;
}

// Time Selection Dialog Component
const TimeSelectionDialog: React.FC<TimeDialogProps> = ({
  open,
  waiterId,
  waiterName,
  type,
  initialTime,
  onClose,
  onConfirm
}) => {
  const [selectedTime, setSelectedTime] = useState<string>(
    initialTime || format(new Date(), 'HH:mm')
  );

  // Generate time options every 30 minutes
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of [0, 30]) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = format(new Date().setHours(hour, minute), 'hh:mm a');
        options.push({ value: timeString, label: displayTime });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          {type === 'checkin' ? <CheckInIcon color="success" /> : <CheckOutIcon color="warning" />}
          <Typography variant="h6">
            {type === 'checkin' ? 'Check In' : 'Check Out'} - {waiterName}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Select the {type === 'checkin' ? 'check-in' : 'check-out'} time:
          </Typography>
          
          <TextField
            select
            fullWidth
            label="Select Time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            SelectProps={{
              native: true,
              MenuProps: {
                style: { maxHeight: 300 }
              }
            }}
          >
            {timeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>

          <TextField
            type="time"
            fullWidth
            label="Or enter custom time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }} // 5 min steps
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color={type === 'checkin' ? 'success' : 'warning'}
          onClick={() => onConfirm(waiterId, selectedTime)}
        >
          Confirm {type === 'checkin' ? 'Check In' : 'Check Out'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WaiterAttendance: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const [waiters, setWaiters] = useState<Waiter[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [waiterStatus, setWaiterStatus] = useState<Record<string, WaiterAttendanceStatus>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  
  // Time dialog state
  const [timeDialog, setTimeDialog] = useState<{
    open: boolean;
    waiterId: string;
    waiterName: string;
    type: 'checkin' | 'checkout';
    initialTime?: string;
  }>({
    open: false,
    waiterId: '',
    waiterName: '',
    type: 'checkin'
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch waiters
  const fetchWaiters = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getWaiters`, formData, config);
      
      if (response.data.status) {
        setWaiters(response.data.data || []);
      }
    } catch (error) {
      console.error("Fetch waiters error:", error);
      showNotification('Failed to fetch waiters', 'error');
    }
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format time for display
  const formatTime = (timeString?: string): string => {
    if (!timeString) return '--:--';
    // If time is in HH:MM:SS format
    if (timeString.includes(':')) {
      const parts = timeString.split(':');
      const hours = parseInt(parts[0]);
      const minutes = parts[1];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    return timeString;
  };

  // Fetch attendance for selected date
  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("date", formatDate(selectedDate));

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getAttendanceByDate`, formData, config);
      
      if (response.data.status) {
        const records = response.data.data?.attendance || [];
        setAttendanceRecords(records);
        
        // Build status map
        const statusMap: Record<string, WaiterAttendanceStatus> = {};
        
        records.forEach((record: AttendanceRecord) => {
          statusMap[record.waiter_id] = {
            waiterId: record.waiter_id,
            hasCheckedIn: true,
            hasCheckedOut: !!record.check_out_time,
            record: record,
            checkInTime: record.check_in_time,
            checkOutTime: record.check_out_time
          };
        });
        
        setWaiterStatus(statusMap);
      }
    } catch (error) {
      console.error("Fetch attendance error:", error);
      showNotification('Failed to fetch attendance', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle check in with selected time
  const handleCheckIn = async (waiterId: string, time: string) => {
    setProcessingId(waiterId);
    setTimeDialog(prev => ({ ...prev, open: false }));
    
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("attendance_date", formatDate(selectedDate));
      formData.append("waiter_id", waiterId);
      formData.append("status", "checkin");
      formData.append("check_in_time", time);

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/markAttendance`, formData, config);
      
      if (response.data.status) {
        showNotification(`Check-in successful at ${formatTime(time)}`, 'success');
        await fetchAttendance();
      } else {
        showNotification(response.data.message || 'Check-in failed', 'error');
      }
    } catch (error: any) {
      console.error("Check-in error:", error);
      showNotification(error.response?.data?.message || 'Error during check-in', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle check out with selected time
  const handleCheckOut = async (waiterId: string, time: string) => {
    const status = waiterStatus[waiterId];
    if (!status?.hasCheckedIn) {
      showNotification('Cannot check out before checking in', 'warning');
      return;
    }

    setProcessingId(waiterId);
    setTimeDialog(prev => ({ ...prev, open: false }));
    
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("attendance_date", formatDate(selectedDate));
      formData.append("waiter_id", waiterId);
      formData.append("status", "checkout");
      formData.append("check_out_time", time);

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/markAttendance`, formData, config);
      
      if (response.data.status) {
        showNotification(`Check-out successful at ${formatTime(time)}`, 'success');
        await fetchAttendance();
      } else {
        showNotification(response.data.message || 'Check-out failed', 'error');
      }
    } catch (error: any) {
      console.error("Check-out error:", error);
      showNotification(error.response?.data?.message || 'Error during check-out', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  // Open time selection dialog
  const openTimeDialog = (waiterId: string, waiterName: string, type: 'checkin' | 'checkout') => {
    const status = waiterStatus[waiterId];
    const initialTime = type === 'checkin' 
      ? status?.checkInTime 
      : status?.checkOutTime || format(new Date(), 'HH:mm');
    
    setTimeDialog({
      open: true,
      waiterId,
      waiterName,
      type,
      initialTime: initialTime || format(new Date(), 'HH:mm')
    });
  };

  // Get current time
  const getCurrentTime = (): string => {
    return format(new Date(), 'hh:mm a');
  };

  useEffect(() => {
    fetchWaiters();
  }, []);

  useEffect(() => {
    if (waiters.length > 0) {
      fetchAttendance();
    }
  }, [selectedDate, waiters]);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Calculate statistics
  const getStats = () => {
    const total = waiters.length;
    const checkedIn = Object.values(waiterStatus).filter(s => s.hasCheckedIn).length;
    const checkedOut = Object.values(waiterStatus).filter(s => s.hasCheckedOut).length;
    const pending = total - checkedIn;
    
    return { total, checkedIn, checkedOut, pending };
  };

  const stats = getStats();

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main
                    }}
                  >
                    <TimeIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Waiter Attendance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check-in / Check-out with time selection
                    </Typography>
                  </Box>
                  <Chip 
                    icon={<DateRangeIcon />}
                    label={format(selectedDate, 'EEEE, MMMM d, yyyy')}
                    color="primary"
                    variant="outlined"
                  />
                  <Tooltip title="Refresh">
                    <IconButton onClick={fetchAttendance} disabled={loading}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Stats Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography color="text.secondary" variant="body2">Total Waiters</Typography>
                          <Typography variant="h4">{stats.total}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                          <PersonIcon />
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography color="text.secondary" variant="body2">Checked In</Typography>
                          <Typography variant="h4" color="success.main">{stats.checkedIn}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                          <CheckInIcon />
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography color="text.secondary" variant="body2">Checked Out</Typography>
                          <Typography variant="h4" color="warning.main">{stats.checkedOut}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                          <CheckOutIcon />
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography color="text.secondary" variant="body2">Pending</Typography>
                          <Typography variant="h4" color="error.main">{stats.pending}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                          <InfoIcon />
                        </Avatar>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Date Selection */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <DatePicker
                    label="Select Date"
                    value={selectedDate}
                    onChange={(date) => date && setSelectedDate(date)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: 200 }
                      }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Current time: {getCurrentTime()}
                  </Typography>
                  <Chip 
                    icon={<InfoIcon />}
                    label="Click time to edit"
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                </Stack>
              </Paper>

              {/* Attendance Table */}
              <Paper sx={{ overflow: 'hidden' }}>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Waiter</TableCell>
                        <TableCell>Check In Time</TableCell>
                        <TableCell>Check Out Time</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : waiters.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                            <Typography color="text.secondary">No waiters found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        waiters.map((waiter) => {
                          const status = waiterStatus[waiter.user_id];
                          const isCheckedIn = status?.hasCheckedIn || false;
                          const isCheckedOut = status?.hasCheckedOut || false;
                          const isProcessing = processingId === waiter.user_id;

                          return (
                            <TableRow key={waiter.user_id} hover>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar 
                                    sx={{ 
                                      width: 32, 
                                      height: 32,
                                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                                      color: theme.palette.primary.main
                                    }}
                                  >
                                    {waiter.fullName.charAt(0)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>
                                      {waiter.fullName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {waiter.email}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                {isCheckedIn ? (
                                  <Chip
                                    icon={<CheckInIcon />}
                                    label={formatTime(status?.checkInTime)}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    onClick={() => !isProcessing && openTimeDialog(waiter.user_id, waiter.fullName, 'checkin')}
                                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) } }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    --
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {isCheckedOut ? (
                                  <Chip
                                    icon={<CheckOutIcon />}
                                    label={formatTime(status?.checkOutTime || undefined)}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    onClick={() => !isProcessing && openTimeDialog(waiter.user_id, waiter.fullName, 'checkout')}
                                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.1) } }}
                                  />
                                ) : isCheckedIn ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Pending
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    --
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {isCheckedOut ? (
                                  <Chip
                                    label="Completed"
                                    size="small"
                                    color="success"
                                  />
                                ) : isCheckedIn ? (
                                  <Chip
                                    label="Checked In"
                                    size="small"
                                    color="info"
                                  />
                                ) : (
                                  <Chip
                                    label="Not Started"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  {!isCheckedIn ? (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      startIcon={<CheckInIcon />}
                                      onClick={() => openTimeDialog(waiter.user_id, waiter.fullName, 'checkin')}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing ? '...' : 'Check In'}
                                    </Button>
                                  ) : !isCheckedOut ? (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="warning"
                                      startIcon={<CheckOutIcon />}
                                      onClick={() => openTimeDialog(waiter.user_id, waiter.fullName, 'checkout')}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing ? '...' : 'Check Out'}
                                    </Button>
                                  ) : (
                                    <Chip
                                      icon={<CheckIcon />}
                                      label="Done"
                                      size="small"
                                      color="success"
                                    />
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Summary */}
              <Paper sx={{ mt: 3, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Today's Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Badge badgeContent={stats.checkedIn} color="success" max={999}>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                          <CheckInIcon />
                        </Avatar>
                      </Badge>
                      <Typography variant="body2" sx={{ mt: 1 }}>Checked In</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Badge badgeContent={stats.checkedOut} color="warning" max={999}>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                          <CheckOutIcon />
                        </Avatar>
                      </Badge>
                      <Typography variant="body2" sx={{ mt: 1 }}>Checked Out</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Badge badgeContent={stats.pending} color="error" max={999}>
                        <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                          <InfoIcon />
                        </Avatar>
                      </Badge>
                      <Typography variant="body2" sx={{ mt: 1 }}>Pending</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

            </Container>

            {/* Time Selection Dialog */}
            <TimeSelectionDialog
              open={timeDialog.open}
              waiterId={timeDialog.waiterId}
              waiterName={timeDialog.waiterName}
              type={timeDialog.type}
              initialTime={timeDialog.initialTime}
              onClose={() => setTimeDialog(prev => ({ ...prev, open: false }))}
              onConfirm={timeDialog.type === 'checkin' ? handleCheckIn : handleCheckOut}
            />

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default WaiterAttendance;